import { useMemo } from 'react';

import { useGetPayorsInfinite, useGetPayorsLogosQuery } from '@helpful/network';

import { TAKE } from './usePayors.const';

type UsePayorsParams = {
  filter?: object;
};

type UsePayorsReturnType = {
  isLoading: boolean;
  hasNextPage: boolean;
  payors: Array<{ id: string; name: string; logo?: string }>;
  fetchNextPage: () => Promise<unknown>;
};

export const usePayors = ({ filter }: UsePayorsParams): UsePayorsReturnType => {
  const { isLoading, data, hasNextPage, fetchNextPage } = useGetPayorsInfinite(
    { skip: 0, take: TAKE, order: 'name', filter },
    {
      query: {
        getNextPageParam: ({ meta: { total } }, all) => {
          const loaded = all.flatMap(({ data }) => data).length;
          if (total > loaded) return TAKE * all.length;
        },
      },
    }
  );

  const payors = useMemo(
    () => data?.pages.flatMap(({ data }) => data) || [],
    [data?.pages]
  );

  const {
    data: {
      payorCollection: { items: logos },
    },
  } = useGetPayorsLogosQuery(
    { slugs: payors.map((item) => item.slug) },
    { initialData: { payorCollection: { items: [] } } }
  );

  const payorsWithLogos = useMemo(
    () =>
      payors.map(({ id, slug, name }) => {
        const logo = logos.find((item) => item.slug === slug)?.logo.url;
        return { id, name, logo };
      }),
    [logos, payors]
  );

  return { isLoading, hasNextPage, payors: payorsWithLogos, fetchNextPage };
};
